import type { Dispatch, FC, SetStateAction } from 'react';
import NextLink from 'next/link';
import {
  Button,
  Divider,
  Flex,
  Link,
  Spinner,
  Text,
} from '@eqi-investimentos/react';
import { Grid } from '@chakra-ui/react';
import { CTA_Button } from '../../interface/CTA_Button';
import {
  ContentMode,
  ContentModeEnum,
  type Content,
} from '@/modules/Content/interface/Content';
import { PATHS } from '@/config/paths';
import { OpenAccountDrawer } from '@/components/modules/OpenAccountDrawer';
import { ButtonScrollVerticalModeText } from '@/modules/News/ui/ButtonScrollVerticalModeText';
import { Thumb } from '@/components/elements/Thumb';
import { IfRender } from '@/utils/jsx';
import { ContentActions } from '../ContentActions';
import { CTAButton } from '../CTAButton';
import { ContentRelatedLinks } from './ContentRelatedLinks';

export const ContentTextMode: FC<ContentTextModeProps> = ({
  content,
  contentMode,
  dataCTAContent,
  isBookmarked,
  isEqi,
  isPending,
  isAdvisoryPage,
  bookmarkContent,
  sendDownloadReportClick,
  sendOpportunity,
  setContentMode,
  t,
}) => {
  return (
    <IfRender condition={contentMode === ContentModeEnum.TEXT}>
      <Grid id={CONTAINER_ID} gridTemplateColumns="1fr max-content" gap="xs">
        <Flex
          flexDir="column"
          w="full"
          maxW="42.25rem"
          p="3xl"
          gap="l"
          borderRadius="xs"
          bgColor="background.brandLight"
        >
          <Text size="h4" fontWeight="medium">
            {content.title}
          </Text>
          <Text size="2xl" noOfLines={3}>
            {content.summary_striped}
          </Text>
          <Flex mt="s" w="full" flexDir="column" gap="2xl">
            <Flex gap="s">
              <NextLink href={`${PATHS.hubs.author}/${content.author_slug}`}>
                <Thumb
                  src={content.author_thumb}
                  width={48}
                  height={48}
                  radius={100}
                />
              </NextLink>
              <Flex flexDir="column" gap="2xs">
                <NextLink href={`${PATHS.hubs.author}/${content.author_slug}`}>
                  <Text size="xl" fontWeight="medium">
                    {content.author_name}
                  </Text>
                </NextLink>
                <Text size="xl" color="content.secondary">
                  {content.published_at} • {content.view_time}
                </Text>
              </Flex>
            </Flex>
            <IfRender condition={!isPending}>
              <ContentActions
                bookmarkContent={bookmarkContent}
                has_audio={content.has_audio}
                has_video={content.has_video}
                isBookmarked={isBookmarked}
                mode="text"
                setContentMode={setContentMode}
              />
            </IfRender>
            <IfRender condition={isPending}>
              <Flex
                justifyContent="center"
                alignItems="center"
                w="3.125rem"
                h="3.125rem"
              >
                <Spinner size="sm" />
              </Flex>
            </IfRender>
          </Flex>
          <Divider borderColor="border.secondary" />
          <IfRender
            condition={
              !isEqi ||
              isAdvisoryPage ||
              !!content.report ||
              !!dataCTAContent?.data
            }
          >
            <Flex gap="m" w="full" mb="2xl" h="3.25rem">
              {isEqi && !!dataCTAContent?.data && (
                <CTAButton data={dataCTAContent.data} />
              )}
              {!isEqi ? (
                <OpenAccountDrawer
                  screen="research"
                  handleBeforeDrawerOpen={sendOpportunity}
                />
              ) : (
                isAdvisoryPage && (
                  <Button size="md" w="12rem" onClick={sendOpportunity}>
                    {t('screens.myAdvisory.speakWithAdvisor')}
                  </Button>
                )
              )}
              {content.report && (
                <Button size="md" variant="outline" w="12rem">
                  <Link
                    download
                    href={content.report}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={sendDownloadReportClick}
                    p="0.75rem 3rem"
                    _hover={{ textDecoration: 'none' }}
                  >
                    {t('screens.content.downloadReport')}
                  </Link>
                </Button>
              )}
            </Flex>
          </IfRender>
          <Flex
            w="full"
            direction="column"
            gap="l"
            color="content.primary"
            fontSize="1rem"
            dangerouslySetInnerHTML={{ __html: content.description }}
            sx={{
              h1: { fontSize: '2rem', fontWeight: 'semibold' },
              h2: { fontSize: '1.5rem', fontWeight: 'semibold' },
              h3: { fontSize: '1.25rem', fontWeight: 'semibold' },
              small: { fontSize: '0.75rem' },
              'strong, b': {
                fontWeight: 'semibold',
              },
              a: {
                textDecoration: 'none',
                color: 'content.brand',
              },
              ul: {
                listStyleType: 'disc',
                ml: '2rem',
              },
              '.thumbs-navigation': {
                display: 'none',
              },
            }}
          />
        </Flex>
        <ButtonScrollVerticalModeText containerId={CONTAINER_ID} />

        <ContentRelatedLinks relatedLinks={content.related_links} />
      </Grid>
    </IfRender>
  );
};

const CONTAINER_ID = 'text-content';

type ContentTextModeProps = {
  content: Content;
  contentMode: ContentMode;
  dataCTAContent?: CTA_Button;
  isBookmarked: boolean;
  isEqi: boolean;
  isPending: boolean;
  isAdvisoryPage: boolean;
  bookmarkContent: () => void;
  sendDownloadReportClick: () => void;
  sendOpportunity: () => void;
  setContentMode: Dispatch<SetStateAction<ContentMode>>;
  t: (key: string) => string;
};
